import React, { useState } from 'react';
import { Plus, Wrench, Zap } from 'lucide-react';
import ResourceSelectionModal from './ResourceSelectionModal';

const ActivityCategoryBlock = ({ 
  category, 
  activities, 
  onAddActivity, 
  onUpdateActivity,
  onDeleteActivity,
  title 
}) => {
  const [showResourceModal, setShowResourceModal] = useState(false);

  const handleAddActivity = (selectedResources) => {
    // Create activities for each selected resource
    selectedResources.forEach(resource => {
      const newActivity = {
        name: resource.name,
        category: resource.category,
        resourceId: resource._id,
        planned: 0,
        completed: 0,
        dailyLog: []
      };
      onAddActivity(newActivity);
    });
  };

  const getCategoryIcon = () => {
    return category === 'Mechanical' ? <Wrench size={20} /> : <Zap size={20} />;
  };

  const getCategoryColor = () => {
    return category === 'Mechanical' 
      ? 'bg-blue-50 border-blue-200' 
      : 'bg-green-50 border-green-200';
  };

  const getCategoryTextColor = () => {
    return category === 'Mechanical' 
      ? 'text-blue-800' 
      : 'text-green-800';
  };

  const getCategoryBadgeColor = () => {
    return category === 'Mechanical' 
      ? 'bg-blue-100 text-blue-800' 
      : 'bg-green-100 text-green-800';
  };

  return (
    <div className={`rounded-lg border-2 ${getCategoryColor()} p-6`}>
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-2">
          {getCategoryIcon()}
          <h3 className={`text-lg font-semibold ${getCategoryTextColor()}`}>
            {title}
          </h3>
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryBadgeColor()}`}>
            {activities.length} activit{activities.length !== 1 ? 'ies' : 'y'}
          </span>
        </div>
        <button
          onClick={() => setShowResourceModal(true)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
            category === 'Mechanical'
              ? 'bg-blue-600 hover:bg-blue-700 text-white'
              : 'bg-green-600 hover:bg-green-700 text-white'
          }`}
        >
          <Plus size={16} />
          Add {category} Activity
        </button>
      </div>

      {/* Activities List */}
      {activities.length === 0 ? (
        <div className="text-center py-8">
          <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4 ${
            category === 'Mechanical' ? 'bg-blue-100' : 'bg-green-100'
          }`}>
            {getCategoryIcon()}
          </div>
          <h4 className={`text-sm font-medium ${getCategoryTextColor()} mb-2`}>
            No {category.toLowerCase()} activities yet
          </h4>
          <p className="text-sm text-gray-500 mb-4">
            Click "Add {category} Activity" to get started
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {activities.map((activity, index) => (
            <div key={index} className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{activity.name}</h4>
                  <div className="mt-2 flex items-center space-x-4 text-sm text-gray-600">
                    <span>Planned: {activity.planned || 0}</span>
                    <span>Completed: {activity.completed || 0}</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryBadgeColor()}`}>
                      {activity.category}
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => onUpdateActivity(index, activity)}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => onDeleteActivity(index)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Resource Selection Modal */}
      <ResourceSelectionModal
        isOpen={showResourceModal}
        onClose={() => setShowResourceModal(false)}
        onSelect={handleAddActivity}
        category={category}
        title={`Select ${category} Resources`}
      />
    </div>
  );
};

export default ActivityCategoryBlock;
